<template>
  <ElBreadcrumb
    :separator="separator"
    :separator-icon="separatorIcon"
  >
    <ElBreadcrumbItem
      v-for="(item, index) in breadcrumbItems"
      :key="item.path || index"
      :to="item.to"
      :replace="item.replace"
    >
      <ElIcon v-if="item.icon" class="breadcrumb-icon">
        <component :is="item.icon" />
      </ElIcon>
      <span>{{ item.title }}</span>
    </ElBreadcrumbItem>
  </ElBreadcrumb>
</template>

<script setup lang="ts">
/**
 * Breadcrumb 面包屑导航组件
 * 基于 Element Plus Breadcrumb 组件封装
 * @author AI Assistant
 * @date 2024-01-01
 */

import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useNavigationStore } from '@/stores/navigation'
import type { RouteLocationRaw } from 'vue-router'

// 面包屑项接口
interface BreadcrumbItem {
  /** 标题 */
  title: string
  /** 路径 */
  path?: string
  /** 路由跳转对象 */
  to?: RouteLocationRaw
  /** 是否替换当前路由 */
  replace?: boolean
  /** 图标 */
  icon?: any
  /** 是否可点击 */
  clickable?: boolean
}

// 接口定义
interface Props {
  /** 分隔符 */
  separator?: string
  /** 分隔符图标 */
  separatorIcon?: any
  /** 面包屑项数据 */
  items?: BreadcrumbItem[]
  /** 是否自动生成面包屑 */
  auto?: boolean
  /** 首页配置 */
  home?: {
    title: string
    path: string
    icon?: any
  }
  /** 是否显示首页 */
  showHome?: boolean
  /** 路由元信息中面包屑的字段名 */
  titleField?: string
  /** 路由元信息中图标的字段名 */
  iconField?: string
}

interface Emits {
  /** 面包屑项点击事件 */
  click: [item: BreadcrumbItem, index: number]
}

// 定义 props
const props = withDefaults(defineProps<Props>(), {
  separator: '/',
  separatorIcon: undefined,
  items: () => [],
  auto: true,
  home: () => ({
    title: '首页',
    path: '/dashboard',
    icon: undefined
  }),
  showHome: true,
  titleField: 'title',
  iconField: 'icon'
})

// 定义 emits
const emit = defineEmits<Emits>()

// 获取路由信息和导航store
const route = useRoute()
const router = useRouter()
const navigationStore = useNavigationStore()

/**
 * 从动态配置中查找对应的配置
 */
const findDynamicConfig = (path: string) => {
  // 安全检查：确保dynamicNavigations存在且为数组
  if (!navigationStore.dynamicNavigations || !Array.isArray(navigationStore.dynamicNavigations)) {
    return null
  }

  for (const nav of navigationStore.dynamicNavigations) {
    // 首页特殊处理
    if (nav.name === 'dashboard' && (path === '/home' || path === '/dashboard')) {
      return {
        title: nav.title,
        icon: nav.icon
      }
    }

    // 查找子项
    const item = nav.items?.find(item => item.path === path)
    if (item) {
      return {
        title: item.title,
        icon: item.icon
      }
    }
  }
  return null
}

/**
 * 自动生成面包屑数据
 */
const generateBreadcrumbs = (): BreadcrumbItem[] => {
  const breadcrumbs: BreadcrumbItem[] = []

  // 添加首页
  if (props.showHome && route.path !== props.home.path) {
    const homeDynamicConfig = findDynamicConfig(props.home.path)
    breadcrumbs.push({
      title: homeDynamicConfig?.title || props.home.title,
      path: props.home.path,
      to: { path: props.home.path },
      icon: homeDynamicConfig?.icon || props.home.icon,
      clickable: true
    })
  }

  // 获取匹配的路由记录
  const matched = route.matched.filter(item => item.meta && item.meta[props.titleField])

  matched.forEach((routeRecord, index) => {
    const meta = routeRecord.meta
    const isLast = index === matched.length - 1

    // 从动态配置中获取最新的标题和图标
    const dynamicConfig = findDynamicConfig(routeRecord.path)

    breadcrumbs.push({
      title: dynamicConfig?.title || meta[props.titleField] as string,
      path: routeRecord.path,
      to: isLast ? undefined : { path: routeRecord.path },
      icon: dynamicConfig?.icon || meta[props.iconField],
      clickable: !isLast
    })
  })

  return breadcrumbs
}

/**
 * 计算面包屑项
 */
const breadcrumbItems = computed((): BreadcrumbItem[] => {
  if (props.auto) {
    return generateBreadcrumbs()
  }
  return props.items
})

/**
 * 监听路由变化，更新面包屑
 */
watch(
  () => route.path,
  () => {
    if (props.auto) {
      // 路由变化时自动更新面包屑
    }
  },
  { immediate: true }
)

/**
 * 监听导航配置更新
 */
watch(
  () => navigationStore.lastUpdateTime,
  (newTime) => {
    if (newTime > 0 && props.auto) {
      // 导航配置更新时重新生成面包屑
    }
  }
)

/**
 * 监听动态导航数据变化
 */
watch(
  () => navigationStore.dynamicNavigations,
  () => {
    if (props.auto) {
      // 动态导航数据变化时重新生成面包屑
    }
  },
  { deep: true }
)

/**
 * 面包屑项点击处理
 */
const handleItemClick = (item: BreadcrumbItem, index: number): void => {
  if (item.clickable !== false && item.to) {
    if (item.replace) {
      router.replace(item.to)
    } else {
      router.push(item.to)
    }
  }
  emit('click', item, index)
}

/**
 * 手动设置面包屑
 */
const setBreadcrumbs = (items: BreadcrumbItem[]): void => {
  // 如果需要手动设置面包屑，可以通过 ref 调用此方法
  // 这里可以添加设置逻辑
}

/**
 * 添加面包屑项
 */
const addBreadcrumb = (item: BreadcrumbItem): void => {
  // 添加面包屑项的逻辑
}

/**
 * 移除面包屑项
 */
const removeBreadcrumb = (index: number): void => {
  // 移除面包屑项的逻辑
}

// 暴露方法
defineExpose({
  setBreadcrumbs,
  addBreadcrumb,
  removeBreadcrumb
})
</script>

<style scoped>
.breadcrumb-icon {
  margin-right: 4px;
  font-size: 14px;
  vertical-align: middle;
}

/* 自定义样式变量 */
:deep(.el-breadcrumb) {
  font-size: 14px;
  line-height: 1;
}

:deep(.el-breadcrumb__item) {
  display: inline-flex;
  align-items: center;
}

:deep(.el-breadcrumb__inner) {
  display: inline-flex;
  align-items: center;
  color: var(--el-text-color-regular);
  font-weight: normal;
  text-decoration: none;
  transition: color 0.2s;
}

:deep(.el-breadcrumb__inner:hover) {
  color: var(--el-color-primary);
  cursor: pointer;
}

:deep(.el-breadcrumb__inner.is-link) {
  color: var(--el-text-color-primary);
  font-weight: 500;
}

:deep(.el-breadcrumb__inner.is-link:hover) {
  color: var(--el-color-primary);
}

:deep(.el-breadcrumb__separator) {
  margin: 0 8px;
  color: var(--el-text-color-placeholder);
  font-weight: 500;
}

:deep(.el-breadcrumb__separator.el-icon) {
  margin: 0 6px;
  font-size: 12px;
}

/* 最后一项样式 */
:deep(.el-breadcrumb__item:last-child .el-breadcrumb__inner) {
  color: var(--el-text-color-primary);
  font-weight: 500;
  cursor: default;
}

:deep(.el-breadcrumb__item:last-child .el-breadcrumb__inner:hover) {
  color: var(--el-text-color-primary);
}

/* 响应式设计 */
@media (max-width: 768px) {
  :deep(.el-breadcrumb) {
    font-size: 13px;
  }

  :deep(.el-breadcrumb__separator) {
    margin: 0 6px;
  }

  .breadcrumb-icon {
    font-size: 13px;
    margin-right: 3px;
  }
}

/* 暗色主题适配 */
@media (prefers-color-scheme: dark) {
  :deep(.el-breadcrumb__inner) {
    color: var(--el-text-color-regular);
  }

  :deep(.el-breadcrumb__inner:hover) {
    color: var(--el-color-primary);
  }

  :deep(.el-breadcrumb__separator) {
    color: var(--el-text-color-disabled);
  }
}

/* 动画效果 */
:deep(.el-breadcrumb__item) {
  animation: breadcrumb-fade-in 0.3s ease-out;
}

@keyframes breadcrumb-fade-in {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* 自定义分隔符样式 */
:deep(.el-breadcrumb__separator[class*="el-icon"]) {
  position: relative;
  top: -1px;
}

/* 长文本省略 */
:deep(.el-breadcrumb__inner) {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 768px) {
  :deep(.el-breadcrumb__inner) {
    max-width: 120px;
  }
}
</style>
